import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { requestAPI, availabilityAPI } from '@/lib/api';
import AppLayout from '@/components/AppLayout';
import RequestCard from '@/components/RequestCard';
import AvailabilityCard from '@/components/AvailabilityCard';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { Skeleton } from '@/components/ui/skeleton';
import { Plus, ChevronDown, ChevronUp, RefreshCw, Calendar, Users, CalendarCheck } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

const Home = () => {
  const navigate = useNavigate();
  const { player } = useAuth();
  const [requests, setRequests] = useState([]);
  const [availability, setAvailability] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [availabilityOpen, setAvailabilityOpen] = useState(false);

  const fetchData = useCallback(async (showRefresh = false) => {
    if (showRefresh) setRefreshing(true);
    try {
      const [requestsRes, availabilityRes] = await Promise.all([
        requestAPI.list(),
        availabilityAPI.list(),
      ]);
      setRequests(requestsRes.data);
      setAvailability(availabilityRes.data);
    } catch (err) {
      console.error('Failed to fetch data:', err);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
    // Poll every 30 seconds for updates
    const interval = setInterval(() => fetchData(), 30000);
    return () => clearInterval(interval);
  }, [fetchData]);

  const handleRespond = async (requestId) => {
    try {
      await requestAPI.respond(requestId);
      fetchData();
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to respond');
    }
  };

  // My requests (I'm the organizer)
  const myRequests = requests.filter((r) => r.organizer_id === player?.id);
  
  // Games I've joined (I have a response - confirmed or interested)
  const myGames = requests.filter((r) => 
    r.organizer_id !== player?.id && 
    r.my_response && 
    (r.my_response.status === 'confirmed' || r.my_response.status === 'interested')
  );
  
  // Open games I haven't responded to yet
  const openGames = requests.filter((r) => 
    r.organizer_id !== player?.id && 
    !r.my_response
  );

  return (
    <AppLayout>
      <div className="max-w-2xl mx-auto space-y-6 pb-24" data-testid="home-page">
        {/* Header */}
        <div className="flex items-center justify-between">
          <h1 className="text-2xl font-bold text-gray-900 dark:text-warm font-serif">
            Find a Game
          </h1>
          <Button
            variant="ghost"
            size="icon"
            onClick={() => fetchData(true)}
            disabled={refreshing}
            data-testid="refresh-btn"
          >
            <RefreshCw className={`w-5 h-5 ${refreshing ? 'animate-spin' : ''}`} />
          </Button>
        </div>

        {/* My Active Requests (games I'm organizing) */}
        {myRequests.length > 0 && (
          <section>
            <h2 className="text-lg font-semibold text-warm-muted mb-3">
              My Requests
            </h2>
            <div className="space-y-3">
              {myRequests.map((request) => (
                <RequestCard
                  key={request.id}
                  request={request}
                  isOrganizer={true}
                  onClick={() => navigate(`/requests/${request.id}`)}
                />
              ))}
            </div>
          </section>
        )}

        {/* My Games (games I've joined) */}
        {myGames.length > 0 && (
          <section>
            <h2 className="text-lg font-semibold text-warm-muted mb-3 flex items-center gap-2">
              <CalendarCheck className="w-5 h-5 text-emerald-400" />
              My Games
            </h2>
            <div className="space-y-3">
              {myGames.map((request) => (
                <RequestCard
                  key={request.id}
                  request={request}
                  isOrganizer={false}
                  myResponse={request.my_response}
                  onClick={() => navigate(`/requests/${request.id}`)}
                />
              ))}
            </div>
          </section>
        )}

        {/* Open Games (games I can join) */}
        <section>
          <h2 className="text-lg font-semibold text-warm-muted mb-3">
            Open Games
          </h2>
          {loading ? (
            <div className="space-y-3">
              {[1, 2, 3].map((i) => (
                <Card key={i}>
                  <CardContent className="p-4">
                    <Skeleton className="h-6 w-3/4 mb-2" />
                    <Skeleton className="h-4 w-1/2 mb-2" />
                    <Skeleton className="h-10 w-24" />
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : openGames.length === 0 ? (
            <Card>
              <CardContent className="p-8 text-center">
                <Users className="w-12 h-12 text-emerald-400/30 mx-auto mb-4" />
                <p className="text-warm-muted">
                  No open games right now
                </p>
                <p className="text-sm text-frost/50 mt-1">
                  Create a request to find players!
                </p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-3">
              {openGames.map((request) => (
                <RequestCard
                  key={request.id}
                  request={request}
                  isOrganizer={false}
                  myResponse={request.my_response}
                  onRespond={() => handleRespond(request.id)}
                  onClick={() => navigate(`/requests/${request.id}`)}
                />
              ))}
            </div>
          )}
        </section>

        {/* Available Players */}
        <Collapsible open={availabilityOpen} onOpenChange={setAvailabilityOpen}>
          <CollapsibleTrigger asChild>
            <Button variant="ghost" className="w-full justify-between p-0 h-auto">
              <h2 className="text-lg font-semibold text-warm-muted">
                Available Players ({availability.length})
              </h2>
              {availabilityOpen ? (
                <ChevronUp className="w-5 h-5" />
              ) : (
                <ChevronDown className="w-5 h-5" />
              )}
            </Button>
          </CollapsibleTrigger>
          <CollapsibleContent className="mt-3">
            {availability.length === 0 ? (
              <Card>
                <CardContent className="p-6 text-center">
                  <p className="text-warm-muted">
                    No one has posted availability yet
                  </p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-3">
                {availability.map((post) => (
                  <AvailabilityCard
                    key={post.id}
                    post={post}
                    onInvite={() => navigate('/requests/new', { state: { club: post.clubs?.[0] } })}
                  />
                ))}
              </div>
            )}
          </CollapsibleContent>
        </Collapsible>

        {/* Floating Action Button */}
        <div className="fixed bottom-24 md:bottom-8 right-4 md:right-8 z-50">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                className="w-16 h-16 rounded-full shadow-lg p-0 flex items-center justify-center text-night hover:scale-105 transition-all"
                style={{ background: 'linear-gradient(135deg, #34d399, #059669)', boxShadow: '0 0 30px rgba(52,211,153,0.3)' }}
                data-testid="fab-btn"
              >
                <Plus className="w-7 h-7" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-48">
              <DropdownMenuItem onClick={() => navigate('/requests/new')}>
                <Users className="w-4 h-4 mr-2" />
                I Need Players
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => navigate('/availability/new')}>
                <Calendar className="w-4 h-4 mr-2" />
                I'm Available
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
    </AppLayout>
  );
};

export default Home;
